import { useMemo } from "react";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import { useWebClientApi } from "src/api";
import {
  toCreateIncomeReq,
  toUpdateIncomeReq,
  type IncomeFormData,
} from "src/api/adapters/income";
import { SlideUpModal } from "src/components/SlideUpModal";
import type { DomainIncomeType } from "@porchpass/api-client";
import { IncomeOptions } from "src/constants";
import {
  type ValidationMapperFunc,
  notEmpty,
  test,
  validate,
  greaterThanMinAmount,
  validDateRange,
} from "src/utils";
import {
  AddMoneyPlusIcon,
  AdditionalIncomeFields,
  FormTable,
  FormTableBody,
} from "@porchpass/ui";
import {
  additionalIncomeErrorsAfterSubmit,
  fromUiAdditionalIncomePatch,
  isIncomeOngoing,
  incomeTypeToDomain,
  toUiAdditionalIncome,
} from "src/forms/adapters/additionalIncome";

export const validationMapper: ValidationMapperFunc<IncomeFormData> = (
  data,
) => ({
  amount: [
    test(notEmpty, "Must enter an amount"),
    test(greaterThanMinAmount(0), "Must be greater than 0"),
  ],
  from_month: [test(notEmpty, "Must select a month")],
  from_year: [test(notEmpty, "Must select a year")],
  to_month: [
    test(
      (val) => isIncomeOngoing(data) || notEmpty(val),
      "Must select a month",
    ),
  ],
  to_year: [
    test((val) => isIncomeOngoing(data) || notEmpty(val), "Must select a year"),
    test(
      () =>
        validDateRange(
          data.from_month ?? "",
          data.from_year ?? "",
          data.to_month ?? "",
          data.to_year ?? "",
        ),
      "End date must be after start date",
    ),
  ],
});

type GenericIncomeModal = {
  incomeItem: Partial<IncomeFormData>;
  closeHandler: () => void;
  confirmHandler: () => void;
};

const titleFromType = (type?: DomainIncomeType) =>
  IncomeOptions.find((item) => item.income_type === type)?.title;

export default ({
  incomeItem,
  closeHandler,
  confirmHandler,
}: GenericIncomeModal) => {
  const api = useWebClientApi();
  const { formData, updateField } = useFormLoad<IncomeFormData>(
    incomeItem,
    () => Promise.resolve({}),
    () => ({}),
    { enabled: false },
  );
  const [submitHandler, loading, , hasAttemptedFormSubmission] =
    useDlaFormSubmit(
      async (data: IncomeFormData) => {
        if (incomeItem.id) {
          return api.updateIncome(incomeItem.id, toUpdateIncomeReq(data));
        }
        return api.createIncome(toCreateIncomeReq(data));
      },
      { onSuccess: () => confirmHandler() },
    );

  const title = titleFromType(formData.income_type);

  const formErrors = validate<IncomeFormData>(formData, validationMapper);

  const incomeValue = useMemo(() => {
    const ui = toUiAdditionalIncome(formData);
    if (formData.income_type) {
      ui.type = incomeTypeToDomain(formData.income_type);
    }
    return ui;
  }, [formData]);

  const incomeErrors = useMemo(
    () =>
      additionalIncomeErrorsAfterSubmit(formErrors, hasAttemptedFormSubmission),
    [formErrors, hasAttemptedFormSubmission],
  );

  const applyIncomeChange = (patch: Partial<typeof incomeValue>) => {
    const mapped = fromUiAdditionalIncomePatch(patch);
    for (const [key, val] of Object.entries(mapped)) {
      updateField(key as keyof IncomeFormData)(val);
    }
  };

  return (
    <SlideUpModal
      title={title ?? ""}
      isOpen={true}
      closeHandler={closeHandler}
      actionButtonText="Add Income"
      actionButtonClickHandler={() => void submitHandler(formData, formErrors)}
      isLoading={loading}
    >
      <FormTable
        title="Income Details"
        headerIcon={
          <AddMoneyPlusIcon
            variant="bulk-rounded"
            size={16}
            className="text-[#BC3C7C]"
          />
        }
      >
        <FormTableBody>
          <AdditionalIncomeFields
            income={incomeValue}
            variant="generic"
            errors={incomeErrors}
            onFieldChange={(field, value) => {
              applyIncomeChange({ [field]: value });
            }}
            onBulkChange={applyIncomeChange}
          />
        </FormTableBody>
      </FormTable>
    </SlideUpModal>
  );
};
